'use client';

import { PlusIcon } from '@heroicons/react/24/outline';
import SafeImage from '@ui/SafeImage';
import { formatCurrency, getCartItemFromProductDocument } from '@util/index';
import { ProductDocument } from 'models/product';
import QuickViewModal from 'features/shop/components/QuickViewModal';
import { useState } from 'react';
import { cva, type VariantProps } from 'class-variance-authority';
import { useShoppingCart } from 'context/ShoppingCartContext';
import Link from 'next/link';

const cardStyles = cva('relative flex w-auto flex-col flex-nowrap gap-y-4', {
  variants: {
    size: {
      default: '',
      small: '',
    },
  },
  defaultVariants: {
    size: 'default',
  },
});

const imageStyles = cva('', {
  variants: {
    size: {
      default: 'h-[26rem] w-[20rem]',
      small: 'h-[15rem] w-[15rem] sm:h-[18rem] sm:w-[18rem]',
    },
  },
  defaultVariants: {
    size: 'default',
  },
});

const priceStyles = cva(
  'font-semibold text-black data-[on-sale=true]:text-brand-secondary',
  {
    variants: {
      size: {
        default: 'text-[2.1rem]',
        small: 'text-[1.6rem]',
      },
    },
    defaultVariants: {
      size: 'default',
    },
  }
);

const quickViewButtonStyles = cva('group absolute cursor-pointer', {
  variants: {
    size: {
      default: 'bottom-[5rem] right-2',
      small: 'bottom-[4rem] right-2',
    },
  },
  defaultVariants: {
    size: 'default',
  },
});

const quickViewIconStyles = cva(
  'flex items-center justify-center rounded-full bg-brand-secondary p-1 text-white shadow shadow-black/30 transition-colors duration-150 ease-linear group-hover:bg-brand-secondary-darker',
  {
    variants: {
      size: {
        default: 'size-12',
        small: 'size-12',
      },
    },
    defaultVariants: {
      size: 'default',
    },
  }
);

type MXUpsellProductCardProps = {
  product: ProductDocument;
  size?: 'default' | 'small';
  isSidebar?: boolean;
} & VariantProps<typeof cardStyles>;

export default function MXUpsellProductCard({
  product,
  size = 'default',
  isSidebar,
}: MXUpsellProductCardProps) {
  const [quickViewOpen, setQuickViewOpen] = useState(false);
  const { increaseCartQty } = useShoppingCart();

  const onAddToCart = () => {
    if (isSidebar) {
      const cartItem = getCartItemFromProductDocument(product);

      increaseCartQty(cartItem);
    } else {
      setQuickViewOpen(true);
    }
  };

  return (
    <>
      <div className="relative">
        <Link
          href={`/product/${product.slug}`}
          className={cardStyles({ size })}
        >
          <SafeImage
            alt={product.title}
            src={product.thumbnail}
            className={`${imageStyles({ size })} ${
              product.thumbnail.includes('wpsstatic') ||
              product.seller_id === 'E16WuBcn0UMejlp0FSdQulSSed12'
                ? 'object-contain'
                : 'object-cover'
            }`}
            height={size === 'default' ? 400 : 300}
            width={size === 'default' ? 200 : 150}
          />

          <div className="flex w-full items-center justify-between">
            <p data-on-sale={product.on_sale} className={priceStyles({ size })}>
              {formatCurrency(product.price)}
            </p>

            {product.on_sale && !product.is_auction && (
              <div
                className={`hidden rounded-full bg-brand-secondary px-4 py-2 font-semibold text-white sm:block ${
                  size === 'small' ? 'text-md' : ''
                }`}
              >
                {Math.round(
                  ((product.og_price - product.price) / product.og_price) * 100
                )}
                % OFF
              </div>
            )}
          </div>
        </Link>
        <button
          type="button"
          aria-label="Quick view this product"
          className={quickViewButtonStyles({ size }) + ' pointer-events-auto'}
          style={{ position: 'absolute', zIndex: 0 }}
          onClick={onAddToCart}
        >
          <div className={quickViewIconStyles({ size })}>
            <PlusIcon className="h-full w-full text-white" />
          </div>
        </button>
      </div>
      <QuickViewModal
        product={product}
        isOpen={quickViewOpen}
        dismiss={() => setQuickViewOpen(false)}
        isSidebar={isSidebar}
      />
    </>
  );
}
